import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiCheckCircle, FiAlertCircle, FiWifiOff } from 'react-icons/fi';
import DashboardLayout from '../../components/layout/DashboardLayout';
import SectionCard from '../../components/ui/SectionCard';
import LoadingScreen from '../../components/ui/LoadingScreen';
import DynamicQuestionField from '../../components/survey/DynamicQuestionField';
import { getSurveyBySlug } from '../../services/dynamicFormService';
import { buildQuestionsByKey, isQuestionVisible, validateVisibleQuestions } from '../../utils/dynamicForm';
import {
  buildVisitPayload,
  enqueueVisit,
  syncQueuedVisits,
  getQueuedVisitsCount,
  VisitQueueFullError,
  MAX_QUEUED_VISITS,
} from '../../services/visitService';

const FORM_SLUG = 'visit-result';

/**
 * Resultado de la visita: formulario dinámico por `slug`
 * (`GET /v1/surveys/visit-result`) para una vivienda puntual
 * (`houseId` en la ruta). El envío (`POST /v1/houses/:id/visits`) es
 * offline-first: se guarda primero en una cola local
 * (`visitService`, máx. {@link MAX_QUEUED_VISITS} sin sincronizar) y
 * se intenta sincronizar en segundo plano. La ubicación GPS está
 * deshabilitada por ahora y se envía siempre en `0`.
 */
export default function VisitCaptureView() {
  const { houseId } = useParams();
  const navigate = useNavigate();

  const [survey, setSurvey] = useState(undefined); // undefined=cargando, null=no encontrado
  const [loadError, setLoadError] = useState(null);
  const [offline, setOffline] = useState(false);
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [submitState, setSubmitState] = useState('idle'); // idle | saving | done | redirecting
  const [submitError, setSubmitError] = useState(null);
  const [syncState, setSyncState] = useState('idle'); // idle | syncing | synced | error
  const [pendingCount, setPendingCount] = useState(0);
  const startedAtRef = useRef(new Date());

  useEffect(() => {
    let active = true;
    startedAtRef.current = new Date();
    setSurvey(undefined);
    setLoadError(null);
    setOffline(!navigator.onLine);
    setAnswers({});
    setErrors({});
    setSubmitState('idle');
    setSubmitError(null);
    setSyncState('idle');

    getSurveyBySlug(FORM_SLUG)
      .then((data) => {
        if (!active) return;
        setSurvey(data);
        if (!navigator.onLine) setOffline(true);
      })
      .catch((err) => {
        if (!active) return;
        setSurvey(null);
        setLoadError(err.message || 'No se pudo cargar el formulario.');
      });

    return () => {
      active = false;
    };
  }, [houseId]);

  const questions = useMemo(() => survey?.questions ?? [], [survey]);
  const questionsByKey = useMemo(() => buildQuestionsByKey(questions), [questions]);
  const visibleQuestions = useMemo(
    () => questions.filter((q) => isQuestionVisible(q, answers, questionsByKey)),
    [questions, answers, questionsByKey]
  );

  if (survey === undefined) {
    return <LoadingScreen label="Cargando formulario…" />;
  }

  function handleAnswerChange(question, value) {
    setAnswers((prev) => ({ ...prev, [question.id]: value }));
    setErrors((prev) => {
      if (!prev[question.id]) return prev;
      const next = { ...prev };
      delete next[question.id];
      return next;
    });
  }

  async function handleSubmit() {
    const validationErrors = validateVisibleQuestions(visibleQuestions, answers);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSubmitError(null);
    setSubmitState('saving');
    try {
      const answersByKey = questions.reduce((acc, question) => {
        if (visibleQuestions.includes(question)) {
          acc[question.key] = answers[question.id] ?? null;
        }
        return acc;
      }, {});
      const visit = buildVisitPayload({ houseId, answersByKey, startedAt: startedAtRef.current });
      enqueueVisit(visit);
      // Si la visita resultó en una conversación con el ciudadano,
      // sincronizamos la visita (o queda en cola local si no hay conexión)
      // y redirigimos a la lista de formularios (del 4 al 7) para que
      // el usuario decida a qué formulario entrar.
      if (answersByKey.visit_result === 'conversation') {
        setSubmitState('redirecting');
        setSyncState('syncing');
        try {
          await syncQueuedVisits();
          setSyncState('synced');
        } catch {
          setSyncState('error');
        }
        setPendingCount(getQueuedVisitsCount());
        navigate(`/data-entry/houses/${houseId}/forms`, { replace: true });
        return;
      }

      // Para cualquier otro resultado, mantenemos sync en segundo plano.
      attemptBackgroundSync();

      setSubmitState('done');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSubmitState('idle');
      setSubmitError(
        err instanceof VisitQueueFullError
          ? err.message
          : err.message || 'No se pudo guardar la visita en este dispositivo.'
      );
    }
  }

  async function attemptBackgroundSync() {
    setSyncState('syncing');
    try {
      await syncQueuedVisits();
      setSyncState('synced');
    } catch {
      setSyncState('error');
    } finally {
      setPendingCount(getQueuedVisitsCount());
    }
  }

  if (!survey) {
    return (
      <DashboardLayout title="Registrar una visita" subtitle="No encontrado">
        {loadError && (
          <div className="form-error" role="alert">
            <FiAlertCircle /> {loadError}
          </div>
        )}
        <SectionCard>
          <p>El formulario solicitado no existe o fue eliminado.</p>
        </SectionCard>
        <Link to="/data-entry/houses" className="btn btn-outline btn-block">
          <FiArrowLeft /> Volver a viviendas
        </Link>
      </DashboardLayout>
    );
  }

  if (submitState === 'done') {
    return (
      <DashboardLayout title="¡Listo!" subtitle={survey.label}>
        <SectionCard>
          <div className="survey-success">
            <FiCheckCircle className="survey-success-icon" />
            <p className="empty-state__title">Visita guardada</p>
            <p className="empty-state__description">
              {syncState === 'synced' && 'La información ya se sincronizó con el servidor.'}
              {syncState === 'syncing' && 'Sincronizando con el servidor…'}
              {syncState === 'error' &&
                `Se guardó en este dispositivo. Aún no hay conexión para sincronizar (${pendingCount}/${MAX_QUEUED_VISITS} pendientes).`}
              {syncState === 'idle' && 'La información quedó guardada en este dispositivo.'}
            </p>
          </div>
        </SectionCard>
        <Link to="/data-entry/houses" className="btn btn-primary btn-block">
          Volver a viviendas
        </Link>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={survey.label} subtitle={survey.description}>
      {offline && (
        <div className="form-error form-error--muted" role="status">
          <FiWifiOff /> Sin conexión: mostrando una copia guardada de este formulario.
        </div>
      )}

      {submitError && (
        <div className="form-error" role="alert">
          <FiAlertCircle /> {submitError}
        </div>
      )}

      {questions.length === 0 ? (
        <SectionCard>
          <p>Este formulario todavía no tiene preguntas configuradas.</p>
        </SectionCard>
      ) : (
        <SectionCard>
          {visibleQuestions.map((question) => (
            <DynamicQuestionField
              key={question.id}
              question={question}
              value={answers[question.id]}
              error={errors[question.id]}
              onChange={(value) => handleAnswerChange(question, value)}
            />
          ))}
        </SectionCard>
      )}

      <div className="survey-nav">
        <button
          type="button"
          className="btn btn-primary btn-block"
          onClick={handleSubmit}
          disabled={submitState === 'saving' || submitState === 'redirecting'}
        >
          {submitState === 'saving' && 'Guardando…'}
          {submitState === 'redirecting' && 'Continuando…'}
          {submitState === 'idle' && (
            <>
              <FiCheckCircle /> Guardar visita
            </>
          )}
        </button>
      </div>
    </DashboardLayout>
  );
}
